/**
 * BookingCheckoutPage
 * ====================
 * Client lands here from Claude after `book_session` creates an mcp_pending booking.
 * Shows the booking summary and redirects to Stripe for payment.
 *
 * Route: /booking/:bookingId/checkout
 *
 * SEC-44: the API needs the buyer's session OR the signed checkout token
 * (`?t=`) that every server-issued checkout link carries. `api` attaches the
 * session and refreshes it on 401; the token is forwarded as-is. Without
 * either the API answers 404, so a signed-out visitor is offered sign-in.
 */

import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Calendar,
  Clock,
  DollarSign,
  User,
  Brain,
  MessageSquare,
  CreditCard,
  CheckCircle2,
  AlertTriangle,
  Loader2,
  ExternalLink,
  ArrowLeft,
  ArrowRight,
  Sparkles,
  ShieldCheck,
  FileCheck2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";
import { API, useAuth } from "@/App";
import api from "@/api";
import { ApiError } from "@/lib/apiError";
import { getUserToken } from "@/lib/authStorage";
import NDASignModal from "@/components/NDASignModal";

export default function BookingCheckoutPage() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  // BR-checkout-ack §5.2 — read auth at page-load so the action area can
  // render the right state from the start (not at submit time).
  const { status: authStatus } = useAuth();

  const [checkout, setCheckout] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [needsSignIn, setNeedsSignIn] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [searchParams] = useSearchParams();
  const checkoutToken = searchParams.get("t");

  // ── Legal gates: NDA + Terms of Engagement ──────────────────────────────
  // Both must clear before the Pay button enables. NDA is a clickwrap modal
  // recorded via POST /api/legal/nda (binds to booking_id). Engagement
  // checkbox is a per-booking ToS re-affirmation recorded via
  // POST /api/legal/acceptances (also bound to booking_id).
  const [showNdaModal, setShowNdaModal] = useState(false);
  const [ndaSigned, setNdaSigned] = useState(false);
  const [engagementAccepted, setEngagementAccepted] = useState(false);
  const [recordingAcceptance, setRecordingAcceptance] = useState(false);
  const [acceptanceError, setAcceptanceError] = useState(null);

  // On mount, check if user already signed the NDA for this booking
  // (so they don't have to re-sign on retry/refresh).
  // BR-checkout-ack — uses the shared axios `api` client so the
  // refresh-on-401 interceptor handles expired sessions instead of
  // surfacing raw JSON. Unauth (no token, expired session) silently
  // skips the pre-fill — the modal will handle the auth state on open.
  useEffect(() => {
    if (!bookingId) return;
    if (authStatus !== "authenticated") return;
    api.get(`/legal/nda/${bookingId}`)
      .then((r) => { if (r.data?.signed) setNdaSigned(true); })
      .catch(() => { /* non-blocking; modal handles auth errors on submit */ });
  }, [bookingId, authStatus]);

  async function handleEngagementToggle(checked) {
    setEngagementAccepted(checked);
    if (!checked) return;
    // BR-checkout-ack §5.6 — defence in depth. Auth-check before submit
    // so we don't even attempt the POST while unauthenticated. The submit
    // path also gets the friendly ApiError wrapper for any failures.
    if (authStatus !== "authenticated") {
      setAcceptanceError("Please sign in to confirm engagement terms.");
      setEngagementAccepted(false);
      return;
    }
    setRecordingAcceptance(true);
    setAcceptanceError(null);
    try {
      const text =
        "I confirm this booking is an exchange of opinion and experience under " +
        "Xpert's Terms of Service and Acceptable Use Policy. I understand the " +
        "session may be recorded and AI-processed per the Recording & AI Consent.";
      const enc = new TextEncoder().encode(text);
      const buf = await crypto.subtle.digest("SHA-256", enc);
      const hash = Array.from(new Uint8Array(buf))
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");
      await api.post(`/legal/acceptances`, {
        policy_id: "terms_of_service",
        policy_version: "1.1.0",
        text_hash_at_acceptance: hash,
        booking_id: bookingId,
      });
    } catch (err) {
      const e = ApiError.from(err);
      setAcceptanceError(e.message);
      setEngagementAccepted(false);
    } finally {
      setRecordingAcceptance(false);
    }
  }

  useEffect(() => {
    fetchCheckout();
    // Re-fetch when the URL bookingId changes (back/forward nav between
    // checkouts). `fetchCheckout` is component-local and closes over the
    // latest bookingId; adding it to deps would require useCallback.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId, checkoutToken]);

  const fetchCheckout = async () => {
    try {
      // Use the shared api client so 401s flow through the refresh
      // interceptor + ApiError wrapper. BR-checkout-ack §5.6.
      const res = await api.get(`/bookings/${bookingId}/checkout`, {
        params: checkoutToken ? { t: checkoutToken } : undefined,
      });
      setCheckout(res.data);
    } catch (err) {
      const e = ApiError.from(err);
      // 404 is the common "booking not found" case; everything else
      // gets the friendly default. No raw response body reaches the DOM.
      setError(e.status === 404 ? "Booking not found." : e.message);
      // Expired link or no link token: the buyer can still get in by
      // signing in to the account that made the booking.
      if (e.status === 404 && !getUserToken()) setNeedsSignIn(true);
    } finally {
      setLoading(false);
    }
  };

  const handlePay = () => {
    if (!checkout?.stripe_checkout_url) return;
    setRedirecting(true);
    window.location.href = checkout.stripe_checkout_url;
  };

  // ---------------------------------------------------------------------------
  // Loading
  // ---------------------------------------------------------------------------

  if (loading) {
    return (
      <div className="min-h-screen bg-canvas flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-brand-green animate-spin" />
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Error
  // ---------------------------------------------------------------------------

  if (error) {
    return (
      <div className="min-h-screen bg-canvas flex flex-col items-center justify-center px-4 text-center">
        <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-ink mb-2">Booking not found</h2>
        <p className="text-slateline-500 mb-6">
          {needsSignIn
            ? "This checkout link has expired or is incomplete. Sign in with the account that made the booking to continue."
            : error}
        </p>
        {needsSignIn ? (
          <Button
            onClick={() =>
              navigate(`/login?return_to=${encodeURIComponent(`/booking/${bookingId}/checkout`)}`)
            }
          >
            Sign in to continue
          </Button>
        ) : (
          <Button onClick={() => navigate("/experts")} variant="outline">Browse Experts</Button>
        )}
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Already handled
  // ---------------------------------------------------------------------------

  if (checkout?.already_handled) {
    const paid = checkout.payment_status === "paid" || checkout.status === "confirmed";
    return (
      <div className="min-h-screen bg-canvas flex flex-col items-center justify-center px-4 text-center">
        {paid ? (
          <>
            <CheckCircle2 className="w-12 h-12 text-brand-green mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-ink mb-2">Already Paid</h2>
            <p className="text-slateline-500 mb-6">This booking has already been confirmed.</p>
          </>
        ) : (
          <>
            <AlertTriangle className="w-12 h-12 text-amber-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-ink mb-2">Session {checkout.status}</h2>
            <p className="text-slateline-500 mb-6">
              This booking's status is <strong>{checkout.payment_status}</strong> and no longer needs checkout.
            </p>
          </>
        )}
        <Button onClick={() => navigate("/dashboard")}>Go to My Bookings</Button>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Main checkout
  // ---------------------------------------------------------------------------

  const duration = checkout.duration_minutes;
  const durationLabel = duration
    ? duration >= 60
      ? `${Math.floor(duration / 60)}h${duration % 60 ? ` ${duration % 60}m` : ""}`
      : `${duration} min`
    : null;

  return (
    <div className="min-h-screen bg-canvas flex flex-col">

      {/* Minimal header */}
      <div className="border-b border-slateline-200 px-6 py-4">
        <div className="max-w-2xl mx-auto flex items-center gap-3">
          <button
            type="button"
            onClick={() => {
              if (window.history.length > 1) navigate(-1);
              else navigate("/experts");
            }}
            className="text-slateline-500 hover:text-ink p-1 -ml-1 transition-colors"
            title="Back"
            aria-label="Back"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="w-7 h-7 rounded-sm bg-brand-green flex items-center justify-center">
            <span className="text-black font-bold text-xs">X</span>
          </div>
          <span className="text-ink font-semibold">Xpert</span>
          <span className="text-slateline-300 mx-2">·</span>
          <span className="text-slateline-500 text-sm">Secure Checkout</span>
        </div>
      </div>

      <div className="flex-1 flex items-start justify-center px-4 py-12">
        <div className="w-full max-w-lg">

          {/* MCP badge */}
          <div className="flex items-center gap-2 mb-6 px-3 py-2 bg-brand-green/5 border border-brand-green/20 rounded-sm w-fit">
            <Sparkles className="w-4 h-4 text-brand-green" />
            <span className="text-brand-green text-xs">Booked via Claude</span>
          </div>

          {/* Card */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-canvas-card border border-slateline-200 rounded-sm overflow-hidden"
          >
            {/* Header */}
            <div className="px-6 pt-6 pb-4 border-b border-slateline-200">
              <h1 className="text-xl font-bold text-ink mb-1">Confirm Your Booking</h1>
              <p className="text-slateline-500 text-sm">Review the details and complete payment to confirm.</p>
            </div>

            {/* Details */}
            <div className="p-6 space-y-4">
              <div className="flex items-center gap-3">
                <User className="w-5 h-5 text-brand-green flex-shrink-0" />
                <div>
                  <p className="text-xs text-slateline-400">Expert</p>
                  <p className="font-medium text-ink">{checkout.expert_name || "Expert"}</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-brand-green flex-shrink-0" />
                <div>
                  <p className="text-xs text-slateline-400">Date</p>
                  <p className="font-medium text-ink">
                    {checkout.scheduled_date
                      ? new Date(checkout.scheduled_date).toLocaleDateString("en-US", {
                          weekday: "long", year: "numeric", month: "long", day: "numeric",
                        })
                      : "—"}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-brand-green flex-shrink-0" />
                <div>
                  <p className="text-xs text-slateline-400">Time (UTC) · Duration</p>
                  <p className="font-medium text-ink">
                    {checkout.scheduled_time || "—"}
                    {durationLabel ? ` · ${durationLabel}` : ""}
                  </p>
                </div>
              </div>

              {checkout.notes && (
                <div className="flex items-start gap-3">
                  <MessageSquare className="w-5 h-5 text-brand-green flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs text-slateline-400">Topic</p>
                    <p className="text-slateline-600 text-sm">{checkout.notes}</p>
                  </div>
                </div>
              )}

              {/* AI Context preview */}
              {checkout.context_preview && (
                <div className="mt-2 p-4 bg-canvas border border-brand-green/10 rounded-sm">
                  <div className="flex items-center gap-2 text-brand-green mb-2">
                    <Brain className="w-4 h-4" />
                    <span className="text-xs font-medium">Context from your Claude conversation</span>
                  </div>
                  {checkout.context_preview.user_goal && (
                    <p className="text-slateline-600 text-sm">
                      <span className="text-slateline-400">Goal: </span>
                      {checkout.context_preview.user_goal}
                    </p>
                  )}
                </div>
              )}

              {/* Total */}
              <div className="flex items-center justify-between pt-4 border-t border-slateline-200">
                <div className="flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-brand-green" />
                  <span className="text-slateline-600">Total</span>
                </div>
                <span className="text-2xl font-bold text-ink">
                  ${checkout.amount_usd?.toFixed(2) || "—"}
                </span>
              </div>
            </div>

            {/* Legal gates — both required before payment */}
            <div className="px-6 pb-2 space-y-3">
              {/* NDA */}
              <div className={`border rounded-sm p-4 ${ndaSigned ? "border-brand-green/30 bg-brand-green/5" : "border-slateline-200 bg-canvas"}`}>
                <div className="flex items-start gap-3">
                  <ShieldCheck className={`w-5 h-5 flex-shrink-0 mt-0.5 ${ndaSigned ? "text-brand-green" : "text-slateline-400"}`} />
                  <div className="flex-1 min-w-0">
                    {ndaSigned ? (
                      <>
                        <p className="text-sm font-medium text-ink">Confidentiality acknowledgement signed</p>
                        <p className="text-xs text-slateline-500 mt-0.5">Recorded with date, IP, and a hash of the text shown to you.</p>
                      </>
                    ) : (
                      <>
                        <p className="text-sm font-medium text-ink">Confidentiality acknowledgement</p>
                        <p className="text-xs text-slateline-500 mt-0.5 mb-2">
                          Required: before payment, both parties acknowledge the session is confidential and recordings are AI-processed per the published consent notice.
                        </p>
                        <Button
                          size="sm"
                          onClick={() => setShowNdaModal(true)}
                          className="bg-brand-green hover:bg-brand-greenHover text-white rounded-md"
                        >
                          Read &amp; sign
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Terms of Engagement */}
              <label className={`flex items-start gap-3 cursor-pointer border rounded-sm p-4 ${engagementAccepted ? "border-brand-green/30 bg-brand-green/5" : "border-slateline-200 bg-canvas"}`}>
                <input
                  type="checkbox"
                  checked={engagementAccepted}
                  disabled={recordingAcceptance}
                  onChange={(e) => handleEngagementToggle(e.target.checked)}
                  className="mt-1 w-4 h-4 accent-brand-green flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-ink flex items-center gap-2">
                    <FileCheck2 className="w-4 h-4 text-brand-green" />
                    Terms of this engagement
                  </p>
                  <p className="text-xs text-slateline-600 mt-1 leading-relaxed">
                    I confirm this booking is an exchange of <span className="italic">opinion and experience</span>, not professional advice, under Xpert's{" "}
                    <a href="/terms" target="_blank" rel="noreferrer" className="text-brand-green underline">Terms of Service</a> and{" "}
                    <a href="/legal/acceptable-use" target="_blank" rel="noreferrer" className="text-brand-green underline">Acceptable Use Policy</a>.
                    Refunds and cancellation follow the rules in those documents.
                  </p>
                  {acceptanceError && (
                    <p className="text-xs text-red-600 mt-2">{acceptanceError}</p>
                  )}
                </div>
              </label>
            </div>

            {/* CTA */}
            <div className="px-6 pb-6">
              {checkout.stripe_checkout_url ? (
                <Button
                  onClick={handlePay}
                  disabled={redirecting || !ndaSigned || !engagementAccepted}
                  className="w-full bg-brand-green hover:bg-brand-greenHover text-white font-semibold py-6 text-base disabled:opacity-50 disabled:cursor-not-allowed"
                  title={!ndaSigned ? "Please sign the confidentiality acknowledgement first." : !engagementAccepted ? "Please accept the engagement terms first." : ""}
                >
                  {redirecting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Redirecting to payment…
                    </>
                  ) : (!ndaSigned || !engagementAccepted) ? (
                    <>Complete the legal steps above to continue</>
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5 mr-2" />
                      Pay ${checkout.amount_usd?.toFixed(2)} securely
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>
              ) : (
                <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-sm text-center">
                  <AlertTriangle className="w-5 h-5 text-amber-400 mx-auto mb-2" />
                  <p className="text-amber-300 text-sm">Payment link not yet available. Please try again shortly.</p>
                  <Button
                    onClick={fetchCheckout}
                    variant="outline"
                    size="sm"
                    className="mt-3 border-amber-500/40 text-amber-400"
                  >
                    Refresh
                  </Button>
                </div>
              )}

              <p className="text-xs text-slateline-300 text-center mt-3">
                Secured by Stripe · Cancel anytime before 24h prior to session
              </p>
            </div>
          </motion.div>
        </div>
      </div>

      {/* NDA modal — opens when user clicks "Read & sign" above */}
      <NDASignModal
        bookingId={bookingId}
        open={showNdaModal}
        onClose={() => setShowNdaModal(false)}
        onSigned={() => {
          setNdaSigned(true);
          setShowNdaModal(false);
        }}
      />
    </div>
  );
}
